import { Card, Flex, HStack, Box, Text } from '@chakra-ui/react';
import React from 'react';
import {
  ScanIcon,
  ChargeIcon,
  DisplayIcon,
  DefaultIcon,
} from '@/components/transaction/transaction-icons';

export interface Transaction {
  code: string;
  type: '00' | '01' | '02'; // 00: チャージ, 01: 支払い, 02: 送金
  title: string;
  transactionDate: string; // ISO 8601形式の日時文字列
  amount: number;
}

// UI用の型変換用ヘルパー
export type TransactionUIType = 'charge' | 'payment' | 'transfer';

export const getTransactionUIType = (type: Transaction['type']): TransactionUIType => {
  switch (type) {
    case '00':
      return 'charge';
    case '01':
      return 'payment';
    case '02':
      return 'transfer';
    default:
      return 'payment';
  }
};

interface TransactionCardProps {
  transaction: Transaction;
  onClick?: (transaction: Transaction) => void;
}

export const TransactionCard: React.FC<TransactionCardProps> = ({ transaction, onClick }) => {
  const uiType = getTransactionUIType(transaction.type);

  const getTransactionIcon = (uiType: TransactionUIType) => {
    switch (uiType) {
      case 'payment':
        return <ScanIcon />;
      case 'charge':
        return <ChargeIcon />;
      case 'transfer':
        return <DisplayIcon />;
      default:
        return <DefaultIcon />;
    }
  };

  const formatAmount = (amount: number) => {
    const prefix = amount === 0 ? '' : amount > 0 ? '+' : '-';
    return `${prefix}฿${Math.abs(amount).toLocaleString()}`;
  };

  const getAmountColor = (uiType: TransactionUIType) => {
    return uiType === 'charge' ? 'green.600' : 'red.600';
  };

  const formatDateTime = (datetime: string) => {
    const date = new Date(datetime);
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')}`;
    const timeStr = `${String(date.getHours()).padStart(2, '0')}:${String(
      date.getMinutes()
    ).padStart(2, '0')}`;
    return { date: dateStr, time: timeStr };
  };

  const handleClick = () => {
    if (onClick) {
      onClick(transaction);
    }
  };

  const { date, time } = formatDateTime(transaction.transactionDate);

  return (
    <Card.Root
      variant="outline"
      borderRadius="lg"
      bg="white"
      _hover={{ bg: 'gray.50', shadow: 'sm' }}
      transition="all 0.2s"
      cursor={onClick ? 'pointer' : 'default'}
      onClick={handleClick}
    >
      <Card.Body p={3}>
        <Flex justify="space-between" align="center">
          <HStack gap={2} flex={1} minW={0}>
            <Box flexShrink={0}>{getTransactionIcon(uiType)}</Box>
            <Box flex={1} minW={0}>
              <Text fontWeight="semibold" color="gray.800" fontSize="sm" truncate>
                {transaction.title}
              </Text>
              <HStack gap={2} mt={0.5}>
                <Text fontSize="xs" color="gray.500">
                  {date} {time}
                </Text>
              </HStack>
            </Box>
          </HStack>
          <Text
            fontWeight="bold"
            fontSize="md"
            color={getAmountColor(uiType)}
            whiteSpace="nowrap"
            ml={3}
          >
            {formatAmount(transaction.amount)}
          </Text>
        </Flex>
      </Card.Body>
    </Card.Root>
  );
};
